import { z } from 'zod';
import { idSchema, unitKeySchema } from './common.js';
import { allergenSchema } from './profiles.js';
/** Ingredient catalog payloads (Phase 1). Ingredients are a shared, global vocabulary (D5/D12). */
export const INGREDIENT_CATEGORIES = [
    'produce',
    'meat',
    'seafood',
    'dairy',
    'bakery',
    'pantry',
    'frozen',
    'spices',
    'beverages',
    'other',
];
export const ingredientCategorySchema = z.enum(INGREDIENT_CATEGORIES);
export const ingredientNameSchema = z.string().trim().min(1).max(120);
const shelfLifeDaysValue = z.number().int().min(0).max(3650).nullable();
export const shelfLifeDaysSchema = z.object({
    pantry: shelfLifeDaysValue,
    fridge: shelfLifeDaysValue,
    freezer: shelfLifeDaysValue,
});
/** Compact row for autocomplete. `matchedAlias` is present when an alias (not the canonical name) matched. */
export const ingredientSummarySchema = z.object({
    id: idSchema,
    canonicalName: z.string(),
    category: ingredientCategorySchema.nullable(),
    defaultUnit: unitKeySchema,
    matchedAlias: z.string().optional(),
});
/** GET /api/v1/ingredients/search?q=&limit= */
export const ingredientSearchQuerySchema = z.object({
    q: z.string().trim().min(1).max(120),
    limit: z.coerce.number().int().min(1).max(50).default(10),
});
export const ingredientSearchResponseSchema = z.object({
    items: z.array(ingredientSummarySchema),
});
/** Full ingredient. Never carries household attribution (D12). */
export const ingredientDtoSchema = z.object({
    id: idSchema,
    canonicalName: z.string(),
    category: ingredientCategorySchema.nullable(),
    defaultUnit: unitKeySchema,
    densityGPerMl: z.number().positive().nullable(),
    /** Average weight of one whole item ("1 onion"), grams. */
    avgUnitWeightG: z.number().positive().nullable(),
    shelfLifeDays: shelfLifeDaysSchema,
    aliases: z.array(z.string()),
    /**
     * Shared allergens (D31): the classifier, corrected where ≥ 2 households
     * agree. Never says who voted (D12).
     */
    allergens: z.array(allergenSchema),
    /** What this household's planner uses: `allergens` overridden by its own corrections. */
    householdAllergens: z.array(allergenSchema),
    /** This household's own corrections ("contains" / "does not contain"). */
    allergenCorrections: z.array(z.object({ allergen: allergenSchema, present: z.boolean() })),
    createdAt: z.iso.datetime(),
});
/**
 * PUT /api/v1/ingredients/:id/allergens — the caller's household votes on the
 * ingredient's allergens: `present` true = contains, false = does not
 * contain, null = withdraw this household's vote. Votes not listed stay.
 */
export const ingredientAllergenVotesRequestSchema = z.object({
    votes: z
        .array(z.object({ allergen: allergenSchema, present: z.boolean().nullable() }))
        .min(1)
        .max(20)
        .refine((v) => new Set(v.map((x) => x.allergen)).size === v.length, 'each allergen at most once'),
});
/** Grams per whole item; a 10 kg ceiling covers a watermelon or a turkey. */
export const avgUnitWeightGSchema = z.number().positive().max(10000);
/** POST /api/v1/ingredients — find-or-create by name (200 when it already existed, 201 when created). */
export const createIngredientRequestSchema = z.object({
    name: ingredientNameSchema,
    category: ingredientCategorySchema.optional(),
    defaultUnit: unitKeySchema.optional(),
    densityGPerMl: z.number().positive().max(30).optional(),
    avgUnitWeightG: avgUnitWeightGSchema.optional(),
});
/**
 * PATCH /api/v1/ingredients/:id — any household may fill values that are still
 * unset (category/density/unit weight/shelf life null, defaultUnit still the default
 * `count`); overwriting a set value requires admin and is audit-logged.
 * A non-admin overwrite attempt is rejected as a whole with 409
 * `INGREDIENT_FIELD_LOCKED` (nothing is applied).
 */
export const updateIngredientRequestSchema = z
    .object({
    category: ingredientCategorySchema.optional(),
    defaultUnit: unitKeySchema.optional(),
    densityGPerMl: z.number().positive().max(30).optional(),
    avgUnitWeightG: avgUnitWeightGSchema.optional(),
    shelfLifeDays: shelfLifeDaysSchema.partial().optional(),
})
    .refine((v) => v.category !== undefined ||
    v.defaultUnit !== undefined ||
    v.densityGPerMl !== undefined ||
    v.avgUnitWeightG !== undefined ||
    (v.shelfLifeDays !== undefined && Object.keys(v.shelfLifeDays).length > 0), 'Provide at least one field to update');
/** POST /api/v1/ingredients/:id/aliases */
export const addAliasRequestSchema = z.object({ alias: ingredientNameSchema });
/** POST /api/v1/ingredients/:id/merge (admin): folds :id into `intoId`. */
export const mergeIngredientRequestSchema = z.object({ intoId: idSchema });
export const ingredientCategoriesResponseSchema = z.object({
    categories: z.array(ingredientCategorySchema),
});
//# sourceMappingURL=ingredients.js.map